import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { CreateEventOrderDto } from '@/types/dtos/orders/request/create-event-order.dto.ts';
import type { OrderDto } from '@/types/dtos/orders/response/order.dto.ts';
import { GET_EVENT_ORDERS_QUERY_KEY } from '@/state/queries/orders/use-get-event-orders.ts';
import { GET_EVENTS_QUERY_KEY } from '@/state/queries/events/use-events.ts';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { EVENT_CATALOG_ORDERS_FEATURE } from '@/constants/feature-flags';

export const useCreateEventOrderMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const { hasAccess } = useFeatureAccess(EVENT_CATALOG_ORDERS_FEATURE);

  return useMutation({
    mutationFn: async (newOrder: CreateEventOrderDto) => {
      if (!hasAccess) throw new Error('Event catalog orders are not available');
      const token = await getToken();
      return createEventOrder(newOrder, token ?? '');
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_ORDERS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENTS_QUERY_KEY],
        }),
      ]);
    },
  });
};

const createEventOrder = async (newOrder: CreateEventOrderDto, token: string): Promise<OrderDto> => {
  const { eventId, ...body } = newOrder;
  const url = `${import.meta.env.VITE_API_URL}/orders/event/${eventId}`;
  return sendJSONRequest({ url, method: METHOD.POST, body, token });
};

export default useCreateEventOrderMutation;
